import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users } from "lucide-react";

const CATEGORY_LABELS = {
  music: "🎵 Musique",
  humor: "😂 Humour",
  cinema: "🎬 Cinéma",
  dance: "💃 Danse",
  art: "🎨 Art",
  fashion: "👗 Mode",
  food: "🍲 Cuisine",
  other: "⭐ Autre",
};

const formatCategory = (cat) => CATEGORY_LABELS[cat] || null;

export const ArtistCard = ({ artist, variant = "default" }) => {
  // Fallback automatique si l'image renvoie 404 (assets OVH perdus)
  const [heroImgOk, setHeroImgOk] = useState(true);
  const [imgOk, setImgOk] = useState(true);
  const showHeroImg = artist.picture && heroImgOk;
  const showImg = artist.picture && imgOk;

  if (variant === "hero") {
    return (
      <Link
        to={`/artist/${artist.user_id}`}
        className="group relative overflow-hidden rounded-2xl col-span-2 row-span-2 bg-gradient-to-br from-brand-red/30 via-brand-orange/20 to-brand-yellow/10 border border-white/10 p-8 flex flex-col justify-end min-h-[360px]"
        data-testid={`artist-hero-${artist.user_id}`}
      >
        {showHeroImg && (
          <img src={artist.picture} alt={artist.name} onError={() => setHeroImgOk(false)} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/60 to-transparent" />
        <div className="relative">
          <p className="text-[10px] tracking-[0.25em] uppercase text-brand-yellow font-bold mb-2">Artiste en vedette</p>
          <h3 className="font-display text-4xl lg:text-5xl font-black tracking-tight mb-2">{artist.name}</h3>
          {artist.country && <p className="text-white/70 flex items-center gap-2"><MapPin size={14} /> {artist.city ? `${artist.city}, ${artist.country}` : artist.country}</p>}
          {artist.genre && <span className="tag-pill mt-3 inline-flex">{artist.genre}</span>}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/artist/${artist.user_id}`}
      className="group flex flex-col items-center gap-3 p-4 rounded-2xl hover:bg-white/[0.03] transition-all duration-300 hover:-translate-y-1"
      data-testid={`artist-card-${artist.user_id}`}
    >
      <div className="relative">
        <div
          className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-red via-brand-orange to-brand-yellow opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 -z-0"
          aria-hidden="true"
        />
        <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden bg-gradient-to-br from-brand-red to-brand-yellow ring-2 ring-transparent group-hover:ring-brand-yellow group-hover:ring-offset-2 group-hover:ring-offset-[#0F0C0B] shadow-xl transition-all duration-300">
          {showImg ? (
            <img src={artist.picture} alt={artist.name} onError={() => setImgOk(false)} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white font-black text-3xl font-display">
              {artist.name?.[0]?.toUpperCase() || "A"}
            </div>
          )}
        </div>
      </div>
      <div className="text-center min-w-0 w-full">
        <p className="font-semibold text-sm md:text-base truncate group-hover:text-white transition-colors">
          {artist.name}
        </p>
        {artist.country && (
          <p className="text-xs text-white/45 truncate flex items-center justify-center gap-1 mt-0.5">
            <MapPin size={10} /> {artist.city ? `${artist.city}, ${artist.country}` : artist.country}
          </p>
        )}
        {formatCategory(artist.category) && (
          <p className="text-[10px] text-brand-orange mt-0.5 truncate" data-testid={`artist-category-${artist.user_id}`}>
            {formatCategory(artist.category)}
          </p>
        )}
        {artist.genre && (
          <p className="text-[10px] text-white/40 mt-0.5 uppercase tracking-wider truncate">
            {artist.genre}
          </p>
        )}
      </div>
    </Link>
  );
};

export default ArtistCard;
